import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, Renderer2, SimpleChanges, inject } from '@angular/core';

// Off-screen canvas context shared across the entire app lifetime,
// used for text measurement without touching the DOM.
const _ctx = (() => document.createElement('canvas').getContext('2d')!)();

@Directive({
  selector: '.ellipsisLeft',
  standalone: true
})
export class EllipsisLeftDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);


  // Maps each host element to its directive instance so the singleton
  // ResizeObserver can dispatch callbacks without per-instance observers.
  private static readonly registry = new Map<Element, EllipsisLeftDirective>();

  // Lazily created on first instantiation; shared across all directive instances.
  private static sharedObserver: ResizeObserver | null = null;

  // rAF batch queue: collects read functions, then flushes reads followed by writes
  // in one animation frame to avoid O(n) forced reflows.
  private static readonly pending: Array<() => (() => void) | null> = [];

  // Tracks the pending rAF handle; null means no flush is currently scheduled.
  private static rafId: number | null = null;

  // Captured from the first injected NgZone so rAF callbacks run outside
  // Angular's change detection.
  private static ngZone: NgZone | null = null;

  // Extra CSS pixels subtracted from the available width to absorb sub-pixel
  // rounding and hinting variance not captured by canvas measurement.
  private static readonly RENDER_MARGIN = 1;

  // Supplies the source text for virtual-scroll recycled views, where the
  // binding value changes without the element being destroyed and re-created.
  // Always prefer this binding over relying on DOM text in virtual-scroll rows.
  @Input() ellipsisText?: string;

  // The untruncated source text. Populated from the DOM on init when no
  // [ellipsisText] binding is present; updated via ngOnChanges otherwise.
  private originalText = '';

  // Cached computed font string; invalidated on resize so getComputedStyle
  // is called only once per layout cycle.
  private cachedFont: string | null = null;

  // Cached letter-spacing in px per character. The CSS font shorthand omits
  // this property, so canvas measureText requires explicit per-character compensation.
  private cachedLetterSpacing = 0;

  // Prevents ngOnChanges from scheduling a measurement before ngAfterViewInit
  // has registered the element with the shared observer.
  private isInitialized = false;

  constructor() {
    const ngZone = inject(NgZone);

    EllipsisLeftDirective.ngZone ??= ngZone;
    EllipsisLeftDirective.sharedObserver ??= EllipsisLeftDirective.createSharedObserver();
  }

  // Separated from the constructor to keep observer callback logic
  // distinct from instance initialisation.
  private static createSharedObserver(): ResizeObserver {
    return new ResizeObserver(entries => {
      entries.forEach(e => {
        const instance = EllipsisLeftDirective.registry.get(e.target as Element);
        if (instance) {
          // Invalidate font cache on resize — container style may have changed.
          instance.cachedFont = null;
          instance.cachedLetterSpacing = 0;
          EllipsisLeftDirective.enqueue(() => instance.readAndWrite());
        }
      });
    });
  }

  ngAfterViewInit(): void {
    // When no [ellipsisText] binding is present, capture the initial DOM text once.
    if (this.ellipsisText === undefined) {
      this.originalText = this.textTarget.textContent?.trim() ?? '';
    }
    this.isInitialized = true;
    EllipsisLeftDirective.registry.set(this.el.nativeElement, this);
    EllipsisLeftDirective.sharedObserver!.observe(this.el.nativeElement);
    this.scheduleAdjust();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ellipsisText'] && this.ellipsisText !== undefined) {
      this.originalText = this.ellipsisText;
      this.cachedFont = null;
      this.cachedLetterSpacing = 0;
      if (this.isInitialized) {
        this.scheduleAdjust();
      }
    }
  }

  ngOnDestroy(): void {
    EllipsisLeftDirective.sharedObserver?.unobserve(this.el.nativeElement);
    EllipsisLeftDirective.registry.delete(this.el.nativeElement);
  }

  // Returns the first child HTMLElement when one exists — writing textContent
  // on the host would destroy child nodes and their event listeners.
  // Falls back to the host element itself when no child is present.
  private get textTarget(): HTMLElement {
    const first = this.el.nativeElement.firstElementChild;
    return first instanceof HTMLElement ? first : this.el.nativeElement;
  }

  private scheduleAdjust(): void {
    EllipsisLeftDirective.enqueue(() => this.readAndWrite());
  }

  // Extends canvas measureText with per-character letter-spacing compensation,
  // which the CSS font shorthand omits and canvas therefore ignores.
  private measureWidth(text: string): number {
    return _ctx.measureText(text).width + this.cachedLetterSpacing * text.length;
  }

  // Read phase: measures text and container width via canvas — no DOM writes.
  // Returns a write closure executed later in the same rAF, after all pending
  // reads have completed, to prevent layout thrashing.
  private readAndWrite(): (() => void) | null {
    const el = this.el.nativeElement;
    const target = this.textTarget;

    // getBoundingClientRect gives sub-pixel precision; offsetWidth rounds up
    // and can leave the last character partially visible at fractional widths.
    const maxWidth = el.getBoundingClientRect().width;
    if (maxWidth === 0) return null;

    if (!this.cachedFont) {
      const style = getComputedStyle(target);
      this.cachedFont = style.font;
      // letter-spacing is absent from the CSS font shorthand, so capture it
      // separately for manual per-character addition in measureWidth.
      this.cachedLetterSpacing = parseFloat(style.letterSpacing) || 0;
    }
    _ctx.font = this.cachedFont;

    const totalWidth = this.measureWidth(this.originalText);
    if (totalWidth <= maxWidth) {
      return () => {
        this.renderer.setProperty(target, 'textContent', this.originalText);
        this.renderer.setAttribute(el, 'data-truncated', 'false');
      };
    }

    const visibleLength = this.calculateVisibleLength(totalWidth, maxWidth);

    return () => {
      // Guard against slice(-0), which returns the full string in JavaScript.
      const tail = visibleLength > 0 ? this.originalText.slice(-visibleLength) : '';
      this.renderer.setProperty(target, 'textContent', '...' + tail);
      this.renderer.setAttribute(el, 'data-truncated', 'true');
    };
  }

  // Returns the maximum number of trailing characters that fit alongside '...'
  // within maxWidth. Starts from a proportional estimate, then corrects with
  // trim/grow loops — typically a few iterations on real-world fonts.
  private calculateVisibleLength(totalWidth: number, maxWidth: number): number {
    const ellipsisWidth = this.measureWidth('...');
    const available = maxWidth - ellipsisWidth;

    // Proportional estimate — fast but imprecise for proportional fonts.
    let visibleLength = available > 0
      ? Math.floor(this.originalText.length * (available / totalWidth))
      : 0;

    // RENDER_MARGIN compensates for canvas-vs-browser measurement gaps.
    const safeMax = maxWidth - EllipsisLeftDirective.RENDER_MARGIN;

    // Trim: corrects overestimates caused by wide trailing characters (Y, T, /).
    while (visibleLength > 0 && this.measureWidth('...' + this.originalText.slice(-visibleLength)) > safeMax) {
      visibleLength--;
    }

    // Grow: corrects underestimates caused by narrow trailing characters (i, l, 1).
    while (visibleLength < this.originalText.length && this.measureWidth('...' + this.originalText.slice(-(visibleLength + 1))) <= safeMax) {
      visibleLength++;
    }

    return visibleLength;
  }

  // Adds a read function to the batch and schedules a single rAF flush outside
  // Angular's zone if one is not already pending, avoiding unnecessary CD cycles.
  private static enqueue(readFn: () => (() => void) | null): void {
    EllipsisLeftDirective.pending.push(readFn);
    if (EllipsisLeftDirective.rafId === null) {
      const run = () => {
        EllipsisLeftDirective.rafId = requestAnimationFrame(() => EllipsisLeftDirective.flush());
      };
      EllipsisLeftDirective.ngZone
        ? EllipsisLeftDirective.ngZone.runOutsideAngular(run)
        : run();
    }
  }

  // Runs all pending reads first, collects their write closures, then executes
  // all writes — the read/write split prevents layout thrashing at scale.
  private static flush(): void {
    EllipsisLeftDirective.rafId = null;
    const batch = EllipsisLeftDirective.pending.splice(0);
    const writes = batch.map(fn => fn());
    writes.forEach(w => w?.());
  }
}
